/**
 * Employee Weekly Summary Modal.
 * Admin / Manager / Office Staff click an employee → see their full week:
 *  - Site time entries
 *  - Travel / driving entries
 *  - Machine driving entries
 *  - Receipts (machine daybook + fuel)
 *  - LAA (Living Away Allowance) days
 *  - Reimbursements
 *  - Signed documents
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Truck, MapPin, FileText, DollarSign, Wrench,
  ChevronLeft, ChevronRight, Receipt, BedDouble, X, ExternalLink, Edit
} from "lucide-react";
import { Button, Card, Badge, Modal, Spinner } from "../shared/ui";
import EditClockedOnModal from "./EditClockedOnModal";
import EditReimbursementModal from "./EditReimbursementModal";
import EditTimesheetEntryModal from "./EditTimesheetEntryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "summary", label: "Summary", icon: Clock },
  { id: "sites", label: "Sites", icon: MapPin },
  { id: "travel", label: "Travel", icon: Truck },
  { id: "machines", label: "Machines", icon: Wrench },
  { id: "receipts", label: "Receipts", icon: Receipt },
  { id: "laa", label: "LAA", icon: BedDouble },
  { id: "reimbursements", label: "Reimburse", icon: DollarSign },
  { id: "documents", label: "Documents", icon: FileText },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" }) : "-");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "-");
const fmtMoney = (v) => `$${(Number(v) || 0).toFixed(2)}`;

// Calculate Monday of the given Date in local time
const mondayOf = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay(); // Sun=0..Sat=6
  const diff = (day + 6) % 7; // Mon=0
  date.setDate(date.getDate() - diff);
  return date;
};

const toIsoDate = (d) => d.toISOString().slice(0, 10);

export const EmployeeWeeklySummaryModal = ({ isOpen, onClose, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("summary");
  const [weekStart, setWeekStart] = useState(() => toIsoDate(mondayOf(new Date())));
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Edit state
  const [editTimeEntry, setEditTimeEntry] = useState(null);    // site clock-on
  const [editTravelEntry, setEditTravelEntry] = useState(null); // travel session
  const [editMachineUsage, setEditMachineUsage] = useState(null);
  const [editReimb, setEditReimb] = useState(null);            // {timesheetId, reimb}
  const [editTsEntry, setEditTsEntry] = useState(null);        // {timesheetId, entry, idx}

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${user.id}/weekly-summary`, {
        params: { week_start: weekStart },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load weekly summary");
    } finally {
      setLoading(false);
    }
  }, [user, weekStart]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  const shiftWeek = (deltaDays) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(toIsoDate(mondayOf(d)));
  };

  if (!isOpen) return null;

  const totals = data?.totals || {};
  // Compute display label from weekStart directly so we don't flash the raw ISO before the fetch resolves
  const _weekStartDate = new Date(weekStart);
  const _weekEndDate = new Date(_weekStartDate);
  _weekEndDate.setDate(_weekEndDate.getDate() + 6);
  const weekLabel = `${fmtDate(_weekStartDate.toISOString())} → ${fmtDate(_weekEndDate.toISOString())}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? `${user.first_name} ${user.last_name} — Weekly Summary` : "Weekly Summary"}
      size="xl"
    >
      <div data-testid="employee-weekly-summary">
        {/* Week navigator */}
        <div className="flex items-center justify-between mb-3 sticky top-0 bg-white z-10 pb-2 border-b">
          <Button size="sm" variant="outline" onClick={() => shiftWeek(-7)} data-testid="weekly-prev-week">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <div className="text-center">
            <p className="text-xs uppercase font-bold text-slate-500">Week</p>
            <p className="font-bold text-sm" data-testid="weekly-week-label">{weekLabel}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => shiftWeek(7)} data-testid="weekly-next-week">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-3 border-b">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`weekly-tab-${t.id}`}
                className={`flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase rounded-t whitespace-nowrap transition-colors ${
                  active
                    ? "bg-orange-500 text-slate-900"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && data && (
          <div className="space-y-3">
            {tab === "summary" && (
              <SummaryView totals={totals} />
            )}
            {tab === "sites" && (
              <SitesList entries={data.time_entries || []} onEdit={setEditTimeEntry} />
            )}
            {tab === "travel" && (
              <TravelList entries={data.travel_entries || []} onEdit={setEditTravelEntry} />
            )}
            {tab === "machines" && (
              <MachinesList entries={data.machine_usages || []} onEdit={setEditMachineUsage} />
            )}
            {tab === "receipts" && (
              <ReceiptsList items={data.receipts || []} onPreview={setLightboxUrl} />
            )}
            {tab === "laa" && (
              <LAAList items={data.laa_entries || []} timesheet={data.timesheet} onEdit={(idx, entry) => setEditTsEntry({ idx, entry, timesheetId: data.timesheet?.id })} />
            )}
            {tab === "reimbursements" && (
              <ReimbursementsList
                items={data.reimbursements || []}
                totals={totals}
                onPreview={setLightboxUrl}
                timesheetId={data.timesheet?.id}
                onEdit={(reimb) => setEditReimb({ timesheetId: data.timesheet?.id, reimb })}
              />
            )}
            {tab === "documents" && (
              <DocumentsList items={data.signed_documents || []} onPreview={setLightboxUrl} />
            )}
          </div>
        )}
      </div>

      {/* Image lightbox — opens data: URLs that browsers refuse in new tab */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
          data-testid="weekly-receipt-lightbox"
        >
          {/^data:application\/pdf|\.pdf$/i.test(lightboxUrl) ? (
            <iframe src={lightboxUrl} title="Receipt" className="w-full h-full max-w-5xl max-h-[90vh] bg-white rounded" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={lightboxUrl} alt="Receipt" className="max-w-full max-h-full object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}

      {/* Inline edit modals */}
      {editTimeEntry && (
        <EditClockedOnModal
          kind="time"
          session={editTimeEntry}
          onClose={() => setEditTimeEntry(null)}
          onSaved={() => { setEditTimeEntry(null); fetchData(); }}
        />
      )}
      {editTravelEntry && (
        <EditClockedOnModal
          kind="travel"
          session={editTravelEntry}
          onClose={() => setEditTravelEntry(null)}
          onSaved={() => { setEditTravelEntry(null); fetchData(); }}
        />
      )}
      {editMachineUsage && (
        <EditClockedOnModal
          kind="machine"
          session={editMachineUsage}
          onClose={() => setEditMachineUsage(null)}
          onSaved={() => { setEditMachineUsage(null); fetchData(); }}
        />
      )}
      {editReimb && editReimb.timesheetId && (
        <EditReimbursementModal
          timesheetId={editReimb.timesheetId}
          reimbursement={editReimb.reimb}
          onClose={() => setEditReimb(null)}
          onSaved={() => { setEditReimb(null); fetchData(); }}
        />
      )}
      {editTsEntry && editTsEntry.timesheetId && (
        <EditTimesheetEntryModal
          timesheetId={editTsEntry.timesheetId}
          entry={editTsEntry.entry}
          entryIndex={editTsEntry.idx}
          onClose={() => setEditTsEntry(null)}
          onSaved={() => { setEditTsEntry(null); fetchData(); }}
        />
      )}
    </Modal>
  );
};

const SummaryStat = ({ label, value, hint, color = "slate", testId }) => {
  const colours = {
    slate: "text-slate-700",
    orange: "text-orange-500",
    blue: "text-blue-600",
    green: "text-green-600",
    purple: "text-purple-600",
    rose: "text-rose-600",
    amber: "text-amber-600",
  };
  return (
    <Card className="text-center py-3" data-testid={testId}>
      <p className="text-xs uppercase font-bold text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${colours[color]}`}>{value}</p>
      {hint != null && <p className="text-[10px] text-slate-500">{hint}</p>}
    </Card>
  );
};

const SummaryView = ({ totals }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="weekly-summary-tab">
    <SummaryStat testId="stat-site-hours" label="Site Hours" value={`${totals.site_hours || 0}h`} color="orange" />
    <SummaryStat testId="stat-travel-hours" label="Travel Hours" value={`${totals.travel_hours || 0}h`} hint={`${totals.total_km || 0} km`} color="blue" />
    <SummaryStat testId="stat-machine-hours" label="Machine Hours" value={`${totals.machine_hours || 0}h`} color="purple" />
    <SummaryStat testId="stat-laa-days" label="LAA Days" value={totals.laa_days || 0} color="amber" />
    <SummaryStat testId="stat-receipts" label="Receipts" value={totals.receipts_count || 0} hint={fmtMoney(totals.receipts_amount)} color="rose" />
    <SummaryStat
      testId="stat-reimburse"
      label="Reimbursements"
      value={fmtMoney(totals.reimbursements_amount)}
      hint={`${fmtMoney(totals.reimbursements_pending)} pending`}
      color="green"
    />
    <SummaryStat testId="stat-docs" label="Signed Docs" value={totals.signed_documents_count || 0} color="blue" />
    <SummaryStat
      testId="stat-timesheet"
      label="Timesheet"
      value={`${totals.timesheet_total_hours || 0}h`}
      hint={`${totals.timesheet_travel_hours || 0}h travel`}
      color="slate"
    />
  </div>
);

const EmptyState = ({ label, testId }) => (
  <p className="text-center text-slate-500 py-8 text-sm" data-testid={testId}>{label}</p>
);

const SitesList = ({ entries, onEdit }) => {
  if (!entries.length) return <EmptyState testId="empty-sites" label="No site time this week." />;
  return (
    <div className="space-y-2" data-testid="sites-list">
      {entries.map((e) => (
        <Card key={e.id} className="flex items-start justify-between gap-3" data-testid={`site-entry-${e.id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm">{e.job_title || "Unassigned"}</p>
            <p className="text-xs text-slate-600">
              {fmtDate(e.clock_in)} · {fmtTime(e.clock_in)} → {e.clock_out ? fmtTime(e.clock_out) : "Active"}
            </p>
            <p className="text-xs text-slate-500">
              {[e.client_name, e.job_location].filter(Boolean).join(" · ") || ""}
            </p>
            {e.machine_name && (
              <Badge variant="info" className="mt-1 text-[10px]">
                <Truck className="w-3 h-3 inline mr-1" />{e.machine_name}
              </Badge>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <p className="font-bold text-orange-500">{e.total_hours ? `${e.total_hours}h` : "—"}</p>
            {!e.clock_out && <Badge variant="success">Active</Badge>}
            {onEdit && (
              <button
                onClick={() => onEdit(e)}
                className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
                title="Edit time entry"
                data-testid={`edit-site-${e.id}`}
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

const TravelList = ({ entries, onEdit }) => {
  if (!entries.length) return <EmptyState testId="empty-travel" label="No travel this week." />;
  return (
    <div className="space-y-2" data-testid="travel-list">
      {entries.map((t) => (
        <Card key={t.id} data-testid={`travel-entry-${t.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-sm">{t.starting_point || "?"} → {t.finishing_point || t.destination || "?"}</p>
                <Badge variant={t.travel_role === "driver" ? "info" : "default"}>{t.travel_role || "driver"}</Badge>
              </div>
              <p className="text-xs text-slate-600">
                {fmtDate(t.clock_in)} · {fmtTime(t.clock_in)} → {t.clock_out ? fmtTime(t.clock_out) : "Active"}
              </p>
              <p className="text-xs text-slate-500">
                {t.vehicle_name || "—"}{t.vehicle_rego ? ` (${t.vehicle_rego})` : ""}
              </p>
              {t.fuel_stops?.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-1">{t.fuel_stops.length} fuel stop(s)</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <p className="font-bold text-blue-600">{t.total_hours ? `${t.total_hours}h` : "—"}</p>
              {t.total_km != null && <p className="text-xs text-slate-500">{t.total_km} km</p>}
              {onEdit && (
                <button
                  onClick={() => onEdit(t)}
                  className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
                  title="Edit travel — km / hours / route"
                  data-testid={`edit-travel-${t.id}`}
                >
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const MachinesList = ({ entries, onEdit }) => {
  if (!entries.length) return <EmptyState testId="empty-machines" label="No machine driving this week." />;
  return (
    <div className="space-y-2" data-testid="machines-list">
      {entries.map((m) => {
        let dur = "—";
        if (m.clock_on_time && m.clock_off_time) {
          const diff = (new Date(m.clock_off_time) - new Date(m.clock_on_time)) / 3600000;
          dur = `${diff.toFixed(1)}h`;
        }
        return (
          <Card key={m.id} data-testid={`machine-entry-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm">{m.machine_name || m.machine_id}</p>
                <p className="text-xs text-slate-600">
                  {fmtDate(m.clock_on_time)} · {fmtTime(m.clock_on_time)} → {m.clock_off_time ? fmtTime(m.clock_off_time) : "Active"}
                </p>
                <p className="text-xs text-slate-500">
                  {[m.client_name, m.job_title].filter(Boolean).join(" · ")}
                </p>
                {(m.start_hours != null || m.end_hours != null) && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    {m.start_hours ?? "?"}h → {m.end_hours ?? "?"}h
                    {m.start_km != null || m.end_km != null ? ` · ${m.start_km ?? "?"}km → ${m.end_km ?? "?"}km` : ""}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <p className="font-bold text-purple-600">{dur}</p>
                {!m.clock_off_time && <Badge variant="success">Active</Badge>}
                {onEdit && (
                  <button
                    onClick={() => onEdit(m)}
                    className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
                    title="Edit machine session"
                    data-testid={`edit-machine-week-${m.id}`}
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

const ReceiptsList = ({ items, onPreview }) => {
  if (!items.length) return <EmptyState testId="empty-receipts" label="No receipts this week." />;
  return (
    <div className="space-y-2" data-testid="receipts-list">
      {items.map((r, idx) => (
        <Card key={`${r.source}-${r.id || idx}`} data-testid={`receipt-${r.id || idx}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-sm">{r.vendor || r.category || "Receipt"}</p>
                <Badge variant="default" className="text-[10px]">{r.source}</Badge>
                {r.needs_reimbursement && (
                  <Badge variant={r.reimbursement_status === "paid" ? "success" : "warning"}>
                    {(r.reimbursement_status || "pending").toUpperCase()}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-600">
                {fmtDate(r.date)} · {r.category || "—"}
                {r.machine_name ? ` · ${r.machine_name}` : ""}
                {r.payment_type ? ` · ${r.payment_type}` : ""}
                {r.litres ? ` · ${r.litres}L` : ""}
              </p>
              {r.notes && <p className="text-[11px] text-slate-500 mt-1">{r.notes}</p>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-bold text-rose-600">{fmtMoney(r.amount)}</p>
              {r.receipt_url && (
                <button
                  type="button"
                  onClick={() => onPreview && onPreview(r.receipt_url)}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                  data-testid={`receipt-link-${r.id || idx}`}
                >
                  <ExternalLink className="w-3 h-3" /> View
                </button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const LAAList = ({ items, timesheet, onEdit }) => {
  if (!items.length) return <EmptyState testId="empty-laa" label="No LAA days claimed for this week." />;
  // Map LAA items back to their index in the timesheet entries
  const tsEntries = timesheet?.entries || [];
  const findIdx = (laa) => tsEntries.findIndex((e) => (e.date === laa.date || e.day === laa.date) && (e.laa || e.living_away_allowance));
  return (
    <div className="space-y-2" data-testid="laa-list">
      {items.map((l, idx) => {
        const tsIdx = findIdx(l);
        const tsEntry = tsIdx >= 0 ? tsEntries[tsIdx] : null;
        return (
          <Card key={idx} data-testid={`laa-${idx}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-sm">{l.job_title || "—"}</p>
                <p className="text-xs text-slate-600">{fmtDate(l.date)} · {l.hours || 0}h</p>
                {l.notes && <p className="text-[11px] text-slate-500">{l.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="warning">LAA</Badge>
                {onEdit && tsEntry && timesheet?.id && (
                  <button
                    onClick={() => onEdit(tsIdx, tsEntry)}
                    className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
                    title="Edit LAA day"
                    data-testid={`edit-laa-${idx}`}
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

const ReimbursementsList = ({ items, totals, onPreview, timesheetId, onEdit }) => {
  if (!items.length) return <EmptyState testId="empty-reimbursements" label="No reimbursements this week." />;
  return (
    <div className="space-y-2" data-testid="reimbursements-list">
      <Card className="bg-green-50 border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase font-bold text-green-700">Total</p>
            <p className="text-2xl font-bold text-green-700">{fmtMoney(totals?.reimbursements_amount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase font-bold text-amber-700">Pending</p>
            <p className="text-2xl font-bold text-amber-700">{fmtMoney(totals?.reimbursements_pending)}</p>
          </div>
        </div>
      </Card>
      {items.map((r, idx) => (
        <Card key={`${r.source}-${r.id || idx}`} data-testid={`reimburse-${r.id || idx}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-sm">{r.description || r.category || r.vendor || "Reimbursement"}</p>
                <Badge variant="default" className="text-[10px]">{r.source}</Badge>
                <Badge variant={(r.status || "pending") === "paid" || r.status === "approved" ? "success" : "warning"}>
                  {(r.status || "pending").toUpperCase()}
                </Badge>
              </div>
              <p className="text-xs text-slate-600">{fmtDate(r.date)} · {r.category || "—"}</p>
              {r.no_receipt_reason && <p className="text-[11px] text-rose-500 mt-1">No receipt: {r.no_receipt_reason}</p>}
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <p className="font-bold text-green-600">{fmtMoney(r.amount)}</p>
              {r.receipt_url && (
                <button
                  type="button"
                  onClick={() => onPreview && onPreview(r.receipt_url)}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                >
                  <ExternalLink className="w-3 h-3" /> Receipt
                </button>
              )}
              {onEdit && r.source === "timesheet" && timesheetId && (
                <button
                  onClick={() => onEdit(r)}
                  className="p-1.5 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700"
                  title="Edit reimbursement description / amount / status"
                  data-testid={`edit-reimburse-${r.id || idx}`}
                >
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const DocumentsList = ({ items, onPreview }) => {
  if (!items.length) return <EmptyState testId="empty-documents" label="No documents signed this week." />;
  return (
    <div className="space-y-2" data-testid="documents-list">
      {items.map((d) => (
        <Card key={d.id} data-testid={`signed-doc-${d.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm">{d.document_name || "Document"}</p>
              <p className="text-xs text-slate-600">{d.job_title || "—"}</p>
              <p className="text-[11px] text-slate-500">
                Signed {fmtDate(d.signed_at)} · {fmtTime(d.signed_at)}
                {d.instance_key ? ` · ${d.instance_key}` : ""}
                {d.signing_frequency ? ` · ${d.signing_frequency}` : ""}
              </p>
            </div>
            {d.file_url && (
              <button
                type="button"
                onClick={() => onPreview && onPreview(d.file_url)}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline flex-shrink-0"
              >
                <ExternalLink className="w-3 h-3" /> PDF
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default EmployeeWeeklySummaryModal;
